import React from 'react';
import { Link } from 'react-router-dom';

const TODAY = '2 May 2026';

const GenericTermsPage = ({ appData }) => {
  const { name, hasSubscription, hasIAP, hasAccount } = appData;

  let section = 0;
  const next = () => ++section;

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Terms of Use</h1>
          <p className="legal-page__subtitle">
            {name} by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: {TODAY}</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>{next()}. Acceptance of Terms</h2>
            <p>
              These Terms of Use ("Terms") govern your access to and use of {name} ("the app", "the
              service"), provided by Wolf Technologies Ltd ("we", "us", "our"), a company registered
              in the United Kingdom. By downloading, installing, or using {name}, you agree to be
              bound by these Terms. If you do not agree, do not use the app.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Eligibility</h2>
            <p>
              You must be at least 13 years old, or the age of digital consent in your jurisdiction,
              to use {name}. By using the app you confirm that you meet this requirement.
              Some apps (including health, fasting and finance apps) require you to be 18 or older —
              consult the app's privacy policy for any age-specific restrictions.
            </p>
          </section>

          {hasAccount && (
            <section className="legal-section">
              <h2>{next()}. Your Account</h2>
              <ul>
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>You agree to provide accurate, current and complete information when creating your account.</li>
                <li>You may delete your account at any time from within the app's settings. Deletion is permanent and irreversible.</li>
                <li>We may suspend or terminate accounts that violate these Terms, including any fraudulent, abusive or illegal activity.</li>
              </ul>
            </section>
          )}

          <section className="legal-section">
            <h2>{next()}. Licence</h2>
            <p>
              Subject to your compliance with these Terms, Wolf Technologies Ltd grants you a
              limited, non-exclusive, non-transferable, revocable licence to download and use {name}
              on devices you own or control, solely for your personal, non-commercial use.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Acceptable Use</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the app.</li>
              <li>Modify, adapt, translate, or create derivative works of the app.</li>
              <li>Use the app for any unlawful purpose or in violation of any applicable law or regulation.</li>
              <li>Use the app to harass, abuse, or harm another person.</li>
              <li>Attempt to gain unauthorised access to the app, our servers, or other users' accounts.</li>
              <li>Use automated systems (bots, scrapers) to access or interact with the app.</li>
              <li>Upload or transmit any malicious code, viruses, or harmful data.</li>
              <li>Resell, sublicense, or redistribute the app or your account.</li>
            </ul>
          </section>

          {(hasSubscription || hasIAP) && (
            <section className="legal-section">
              <h2>{next()}. Purchases &amp; Subscriptions</h2>
              {hasSubscription && (
                <>
                  <h3>Auto-Renewable Subscriptions (iOS)</h3>
                  <p>
                    {name} offers auto-renewable subscriptions purchased through the Apple App Store.
                    The following terms apply to all iOS subscription purchases:
                  </p>
                  <ul>
                    <li><strong>Length &amp; price:</strong> Subscription length and price for each tier are displayed on the in-app purchase screen before you confirm payment. Pricing may vary by region and is shown in your local currency.</li>
                    <li><strong>Payment:</strong> Payment is charged to your Apple ID account at confirmation of purchase.</li>
                    <li><strong>Auto-renewal:</strong> Your subscription automatically renews at the end of each billing period unless you cancel at least 24 hours before the end of the current period.</li>
                    <li><strong>Renewal charge:</strong> Your account will be charged for renewal within 24 hours prior to the end of the current period at the then-current subscription price.</li>
                    <li><strong>Managing your subscription:</strong> You can manage and cancel your subscription by going to Settings &gt; [your name] &gt; Subscriptions on your iOS device after purchase.</li>
                    <li><strong>Free trials:</strong> Any unused portion of a free trial period is forfeited when you purchase a subscription.</li>
                    <li><strong>Refunds:</strong> Apple's Media Services Terms apply to refunds. Requests must be made via Apple at <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">reportaproblem.apple.com</a>.</li>
                  </ul>

                  <h3>Web Subscriptions (Stripe)</h3>
                  <p>
                    Where {name} offers a web subscription, payments are processed by Stripe. Stripe
                    handles all card details — we never see or store your full card number. Web
                    subscriptions auto-renew until cancelled. You may cancel at any time from the
                    Manage Subscription section in your account, and access remains active until the
                    end of the current billing period.
                  </p>
                </>
              )}
              {hasIAP && (
                <>
                  <h3>Non-Subscription In-App Purchases</h3>
                  <p>
                    {name} may offer one-time in-app purchases (for example, additional content or
                    cosmetic items). These purchases are non-refundable except as required by law or
                    the Apple App Store's refund policy. All purchases are final on confirmation.
                  </p>
                </>
              )}
              <p>
                <strong>EU/UK consumer rights:</strong> If you are a consumer based in the EU or UK,
                statutory cancellation and refund rights may apply where required by law. Nothing in
                these Terms limits your statutory rights.
              </p>
            </section>
          )}

          <section className="legal-section">
            <h2>{next()}. Intellectual Property</h2>
            <p>
              All content, features, and functionality in {name} — including software, text, graphics,
              logos, icons, images, audio, and video — are owned by or licensed to Wolf Technologies
              Ltd and are protected by United Kingdom and international copyright, trademark, and
              other intellectual property laws.
            </p>
            <p>
              You retain ownership of any data and content you create or upload within {name} ("Your
              Content"). By using the app, you grant Wolf Technologies Ltd a limited, worldwide,
              royalty-free licence to host, store, process and display Your Content solely for the
              purpose of providing the service to you.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Privacy</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal data is described
              in the {name} Privacy Policy, which forms part of these Terms by reference.
            </p>
            <p>
              <Link to={`/${appData.slug}/privacy`}>Read the Privacy Policy</Link>.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Health, Wellness and Financial Disclaimers</h2>
            <p>
              {name} is provided for informational and tracking purposes only. Where applicable, the
              app does <strong>not</strong> provide medical, psychological, nutritional, legal,
              financial, tax, or investment advice, and is not a substitute for professional
              consultation, diagnosis, treatment, or advice.
            </p>
            <ul>
              <li><strong>Health &amp; wellness apps</strong> (BabyTrack, Drift, Ember, Quench, Nova Cycle, Streaks, HipoApp, PowderLog) — always consult a qualified healthcare professional before making decisions about your health or that of a child.</li>
              <li><strong>Finance apps</strong> (Wealth Tracker) — figures shown are estimates based on data you enter. Always consult a qualified financial adviser before making investment decisions.</li>
              <li><strong>Productivity, games and utility apps</strong> — provided as-is for personal use. Outcomes such as productivity gains or game results are not guaranteed.</li>
            </ul>
            <p>
              Reliance on any information provided by {name} is solely at your own risk.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Third-Party Services &amp; Stores</h2>
            <p>
              {name} is distributed through third-party app stores including the Apple App Store and
              (where available) Google Play. Your use of these stores is governed by their own terms
              of service. Where {name} is downloaded from the Apple App Store, the following
              additional terms apply:
            </p>
            <ul>
              <li>These Terms are between you and Wolf Technologies Ltd only — not Apple. Apple is not responsible for {name} or its content.</li>
              <li>Apple has no obligation to provide maintenance or support for {name}.</li>
              <li>If {name} fails to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any). Apple has no other warranty obligation.</li>
              <li>Apple is not responsible for addressing any product liability, regulatory compliance, or third-party intellectual property claims relating to {name}.</li>
              <li>Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them against you.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{next()}. Disclaimer of Warranties</h2>
            <p>
              {name} is provided <strong>"as is" and "as available"</strong> without warranties of
              any kind, either express or implied, including (but not limited to) implied warranties
              of merchantability, fitness for a particular purpose, non-infringement, or
              uninterrupted operation. We do not warrant that the app will be error-free, secure
              against unauthorised access, or available at all times. Your use of the app is at your
              sole risk.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Wolf Technologies Ltd, its directors,
              employees, and partners shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages — including loss of profits, data, goodwill, or
              other intangible losses — arising from your use of, or inability to use, {name}.
            </p>
            <p>
              Our total liability to you for any claim arising out of or relating to these Terms or
              the app shall not exceed the greater of (a) the amount you paid to us in the 12 months
              preceding the claim, or (b) £50.
            </p>
            <p>
              Nothing in these Terms excludes liability for death or personal injury caused by
              negligence, fraud, or any other liability that cannot lawfully be excluded under UK law.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Wolf Technologies Ltd from any claims,
              damages, liabilities, costs, or expenses (including reasonable legal fees) arising out
              of your breach of these Terms or your misuse of the app.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Termination</h2>
            <p>
              We may suspend or terminate your access to {name} at any time, with or without notice,
              for any breach of these Terms. You may terminate your use of {name} at any time by
              deleting your account (where applicable) and uninstalling the app. Sections of these
              Terms which by their nature should survive termination — including intellectual
              property, disclaimers, limitations of liability, and indemnification — will continue to
              apply after termination.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. If we make material changes, we will
              notify you through the app or by email. Continued use of {name} after the effective
              date of revised Terms constitutes acceptance of the changes.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Governing Law &amp; Jurisdiction</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of England and
              Wales. The courts of England and Wales shall have exclusive jurisdiction over any
              disputes arising out of or in connection with these Terms, except that consumers
              resident in the EU may also bring proceedings in their country of habitual residence.
            </p>
          </section>

          <section className="legal-section">
            <h2>{next()}. Contact</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
            <p>We aim to respond to all enquiries within 48 hours.</p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default GenericTermsPage;
